/**
 * Session values composable
 * Provides centralized access to global Vuex session state
 * Uses singleton pattern to avoid recreating computed refs
 */

import { computed, type ComputedRef } from 'vue'
import { useStore } from 'vuex'
import { authorities } from '@/constants/authorities'
import type { Organisation } from '@/types/models'
import type { StudioToken } from '@/utils/tokenUtils'

/** Return type of useSessionValues composable */
export interface UseSessionValuesReturn {
  language: ComputedRef<string | undefined>
  currency: ComputedRef<string>
  isCurrencyEditable: ComputedRef<boolean | undefined>
  userAuthorities: ComputedRef<string[]>
  hasAdminToolLimitedAuthority: ComputedRef<boolean>
  hasAdminOrSupport: ComputedRef<boolean>
  token: ComputedRef<StudioToken | null>
  userId: ComputedRef<number | null | undefined>
  userRole: ComputedRef<string | null>
  selectedOrganisation: ComputedRef<Organisation | null | undefined>
  selectedOrganisationId: ComputedRef<string | null | undefined>
}

/** Cached singleton state */
let cachedState: UseSessionValuesReturn | null = null

/**
 * Initialize session values from Vuex store
 * Creates computed refs for common session values
 * @returns Session value computed refs
 */
function initializeSessionValues(): UseSessionValuesReturn {
  if (!cachedState) {
    const store = useStore()

    // Fallback if store not available (e.g., during SSR or testing)
    if (!store) {
      const language = computed(() => 'en-US')
      const currency = computed(() => 'EUR')
      const isCurrencyEditable = computed(() => true)
      const userAuthorities = computed(() => [] as string[])
      const hasAdminToolLimitedAuthority = computed(() => false)
      const hasAdminOrSupport = computed(() => false)
      const token = computed(() => null as StudioToken | null)
      const userId = computed(() => null as number | null)
      const userRole = computed(() => null as string | null)
      const selectedOrganisation = computed(() => null as Organisation | null)
      const selectedOrganisationId = computed(() => null as string | null)

      return {
        language,
        currency,
        isCurrencyEditable,
        userAuthorities,
        hasAdminToolLimitedAuthority,
        hasAdminOrSupport,
        token,
        userId,
        userRole,
        selectedOrganisation,
        selectedOrganisationId,
      }
    }

    // Compute session values from store
    const language = computed(() => store.state.locale?.language)
    const currency = computed(() => store.state?.currency ?? 'EUR')
    const isCurrencyEditable = computed(() => store.state?.isCurrencyEditable)
    const userAuthorities = computed(() => store.state?.session?.userAuthorities ?? [])
    const hasAdminToolLimitedAuthority = computed(() =>
      userAuthorities.value.includes(authorities.ADMIN_TOOL_LIMITED_AUTHORITY)
    )
    const token = computed(() => store.state?.session?.studioToken ?? null)
    const userId = computed(() => store.state.session?.userId ?? store.state.session?.user?.userId)
    const userRole = computed(() => store.state.session?.userRole ?? null)
    const hasAdminOrSupport = computed(() => {
      return userRole.value === 'admin' || userRole.value === 'support'
    })
    const selectedOrganisation = computed(() => store.state.session?.selectedOrganisation)
    const selectedOrganisationId = computed(() => store.state.session?.selectedOrganisation?.id)

    cachedState = {
      language,
      currency,
      isCurrencyEditable,
      userAuthorities,
      hasAdminToolLimitedAuthority,
      hasAdminOrSupport,
      token,
      userId,
      userRole,
      selectedOrganisation,
      selectedOrganisationId,
    }
  }

  return cachedState
}

/**
 * Access global session values from Vuex store
 * Singleton composable - returns same computed refs across all components
 * 
 * @returns Reactive session values
 * 
 * @example
 * const { language, currency, userRole } = useSessionValues()
 * if (userRole.value === 'admin') { ... }
 */
export function useSessionValues(): UseSessionValuesReturn {
  return initializeSessionValues()
}